import { FC, useContext, useMemo, useState } from 'react';

import { useLazyQuery } from '@apollo/client';
import { Button, DateRangePicker, InfoBlock } from '@smartshell/ui-kit';
import dayjs from 'dayjs';

import { useSharedDateRange, useWindowResize } from 'hooks';
import { LocalizationContext } from 'localization';
import { formatDateForServer } from 'utils';

import { ChartBlock } from './ChartBlock';
import s from './HostsOccupation.module.scss';
import {
  GENERATE_HOSTS_OCCUPATION_REPORT,
  GENERATE_HOSTS_OCCUPATION_REPORT_FILE,
  IHostOccupationReportFileResponse,
  IHostOccupationReportRequest,
  IHostOccupationReportResponse,
} from './schema';
import { TableBlock } from './TableBlock';
import { ETypeSlice, ISummaryData, TNormalizedItem } from './types';

export const HostsOccupation: FC = () => {
  const { t, lang } = useContext(LocalizationContext);

  const {
    startDate,
    endDate,
    periodType,
    offset,
    setStartDate,
    setEndDate,
    setPeriodType,
    setOffset,
  } = useSharedDateRange();

  const [type, setType] = useState<ETypeSlice>(ETypeSlice.HOSTS);

  const [error, setError] = useState<string | null>(null);

  const [generateReport, { data: response, loading, called, variables }] =
    useLazyQuery<IHostOccupationReportResponse, IHostOccupationReportRequest>(
      GENERATE_HOSTS_OCCUPATION_REPORT,
      {
        fetchPolicy: 'no-cache',
        onError: (error) => {
          setError(error.message);
        },
      }
    );

  const [generateReportFile, { loading: reportFileLoading }] = useLazyQuery<
    IHostOccupationReportFileResponse,
    IHostOccupationReportRequest
  >(GENERATE_HOSTS_OCCUPATION_REPORT_FILE, {
    fetchPolicy: 'no-cache',
    onCompleted: (response) => {
      if (!response) return;

      open(response.hostsOccupationReport.url, '_self');
    },
    onError: (error) => {
      setError(error.message);
    },
  });

  const normalizedData = useMemo(
    () =>
      response?.hostsOccupationReport.data.map<TNormalizedItem>(
        ({ values }, index) => {
          const [
            guestsTime,
            clientsTime,
            totalTime,
            guestsPercent,
            clientsPercent,
            totalPercent,
            averageSessionTime,
            sessionsCount,
            guestsProceeds,
            clientsProceeds,
            totalProceeds,
          ] = values;
          const alias = response.hostsOccupationReport.keys[index];
          const groupTitle = response.hostsOccupationReport.extraLabels[index];

          return {
            alias,
            groupTitle,
            averageSessionTime,
            sessionsCount,
            guests: {
              time: guestsTime,
              percentTime: guestsPercent,
              proceeds: guestsProceeds,
            },
            clients: {
              time: clientsTime,
              percentTime: clientsPercent,
              proceeds: clientsProceeds,
            },
            total: {
              time: totalTime,
              percentTime: totalPercent,
              proceeds: totalProceeds,
            },
          };
        }
      ) ?? [],
    [response]
  );

  const summaryData = useMemo<ISummaryData>(() => {
    const totalsData = {
      totalTime: 0,
      occupationTime: 0,
      occupationPercentage: 0,
      sessionsCount: 0,
      averageSessionsTime: 0,
      money: 0,
    };

    response?.hostsOccupationReport.summary.forEach((item) => {
      if (item.title === 'total working time')
        totalsData.totalTime = item.value;
      if (item.title === 'total occupation time')
        totalsData.occupationTime = item.value;
      if (item.title === 'total occupation percentage')
        totalsData.occupationPercentage = item.value;
      if (item.title === 'total client sessions count')
        totalsData.sessionsCount = item.value;
      if (item.title === 'average client sessions time')
        totalsData.averageSessionsTime = item.value;
      if (item.title === 'total money') totalsData.money = item.value;
    });

    return totalsData;
  }, [response]);

  const visibleData = useMemo<Array<TNormalizedItem>>(() => {
    if (type === ETypeSlice.HOSTS) return normalizedData;

    const availableTime = summaryData.totalTime / normalizedData.length;

    const dataByGroup = normalizedData.reduce<
      Record<string, TNormalizedItem & { hostCount: number }>
    >((acc, item) => {
      if (!acc[item.groupTitle]) {
        acc[item.groupTitle] = {
          ...item,
          alias: '',
          clients: { ...item.clients },
          guests: { ...item.guests },
          total: { ...item.total },
          hostCount: 1,
        };

        return acc;
      }

      acc[item.groupTitle] = {
        alias: '',
        groupTitle: item.groupTitle,
        averageSessionTime:
          acc[item.groupTitle].averageSessionTime + item.averageSessionTime,
        sessionsCount: acc[item.groupTitle].sessionsCount + item.sessionsCount,
        total: {
          time: acc[item.groupTitle].total.time + item.total.time,
          proceeds: acc[item.groupTitle].total.proceeds + item.total.proceeds,
          percentTime: 0,
        },
        clients: {
          time: acc[item.groupTitle].clients.time + item.clients.time,
          proceeds:
            acc[item.groupTitle].clients.proceeds + item.clients.proceeds,
          percentTime: 0,
        },
        guests: {
          time: acc[item.groupTitle].guests.time + item.guests.time,
          proceeds: acc[item.groupTitle].guests.proceeds + item.guests.proceeds,
          percentTime: 0,
        },
        hostCount: acc[item.groupTitle].hostCount + 1,
      };

      return acc;
    }, {});

    return Object.values(dataByGroup).map((item) => {
      const groupTime = availableTime * item.hostCount;

      item.total.percentTime = (item.total.time / groupTime) * 100;
      item.guests.percentTime = (item.guests.time / groupTime) * 100;
      item.clients.percentTime = (item.clients.time / groupTime) * 100;

      item.averageSessionTime = item.averageSessionTime / item.hostCount;

      return item;
    });
  }, [type, summaryData.totalTime, normalizedData]);

  const size = useWindowResize();

  return (
    <div className={s.container} data-onboarding="report-hosts-result">
      {error && (
        <InfoBlock title={t('errors.error')} message={error} direction="row" />
      )}
      <div
        className={s.generate}
        data-onboarding="report-hosts-generate"
        data-onboarding-report-generated={String(
          Boolean(response?.hostsOccupationReport)
        )}
      >
        <DateRangePicker
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          lastValidDate={dayjs().format()}
          firstValidDate={dayjs('2022-03-01T00:00:00').format()}
          lang={lang}
          periodType={periodType}
          setPeriodType={setPeriodType}
          offset={offset}
          setOffset={setOffset}
        />
        <div className={s.buttons_block}>
          <Button
            onClick={() => {
              setError(null);

              const input: IHostOccupationReportRequest['input'] = {
                from: formatDateForServer(dayjs(startDate)),
                to: formatDateForServer(dayjs(endDate)),
              };

              return generateReport({ variables: { input } });
            }}
            disabled={loading}
          >
            {t('reports.generate')}
          </Button>
          <Button
            variant="outline"
            onClick={() => generateReportFile({ variables })}
            disabled={!variables?.input || reportFileLoading}
            className={s.export}
            iconName="IncomeIcon"
          >
            {size.width < 1864 && size.width > 960 ? t('csv') : t('export.csv')}
          </Button>
        </div>
      </div>
      <div className={s.data}>
        <ChartBlock
          data={visibleData}
          summaryData={summaryData}
          loading={loading}
          called={called}
          type={type}
          setType={setType}
        />
        <TableBlock
          type={type}
          data={visibleData}
          loading={loading}
          apiCalled={called}
        />
      </div>
    </div>
  );
};
